import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { CreatedView } from "./CreatedView";
import { CreateView } from "./CreateView";
import { ManageView } from "./ManageView";

import { usePromptTemplateData } from "@/components/PromptTemplate/hooks";
import {
  ShareDialogMode,
  ShareSessionDialogShell,
  defaultPinnedVersionSelection,
  pinnedVersionSelectionToCreateBodyFields,
  useShareSessionDialog,
  type PinnedVersionSelection,
} from "@/components/SharedSessions";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { usePromptVersions } from "@/queries";
import { useSharePrompt } from "@/queries/prompts";
import type { SharedSession } from "@/queries/prompts/types";
import type { UpdateSharedSessionRequest } from "@/queries/prompts/useUpdateSharedSession";

type InteractionMode = "batch" | "chat";

export interface SharePromptButtonProps {
  hideTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const SharePromptButton = ({
  hideTrigger = false,
  open: openProp,
  onOpenChange,
}: SharePromptButtonProps) => {
  const { promptId, versionId } = useParams();
  const { promptName, promptVersion } = usePromptTemplateData(promptId!, versionId);
  const { activeWorkspaceId } = useUser();
  const isCompletionTemplate = promptVersion.data?.prompt_template?.type === "completion";
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const sharePromptMutation = useSharePrompt();

  const numericPromptId = promptId ? Number(promptId) : undefined;

  const promptVersionsQuery = usePromptVersions(userToken, {
    promptRegistryId: numericPromptId,
    workspaceId: activeWorkspaceId!,
  });

  const latestVersionNumber = useMemo(() => {
    const versions =
      promptVersionsQuery.data?.pages.flatMap(page => page.prompt_versions ?? []) ?? [];
    return versions.length > 0 ? Math.max(...versions.map(v => v.number)) : undefined;
  }, [promptVersionsQuery.data]);

  const currentVersionNumber = versionId ? Number(versionId) : undefined;
  const defaultVersionSelection = useMemo(
    () => defaultPinnedVersionSelection(currentVersionNumber, latestVersionNumber),
    [currentVersionNumber, latestVersionNumber],
  );

  const [interactionMode, setInteractionMode] = useState<InteractionMode>("chat");
  const [remixEnabled, setRemixEnabled] = useState(true);

  const resetExtraFormState = useCallback(() => {
    setInteractionMode("chat");
    setRemixEnabled(true);
  }, []);

  const buildCreateVariables = useCallback(
    (ctx: { name: string; is_public: boolean; versionSelection: PinnedVersionSelection }) => {
      if (!activeWorkspaceId || !numericPromptId) {
        return null;
      }

      const effectiveMode: InteractionMode = isCompletionTemplate ? "batch" : interactionMode;
      return {
        workspace_id: activeWorkspaceId,
        name: ctx.name,
        prompt_id: numericPromptId,
        is_public: ctx.is_public,
        batch_enabled: effectiveMode === "batch",
        chat_enabled: effectiveMode === "chat",
        remix_enabled: remixEnabled,
        ...pinnedVersionSelectionToCreateBodyFields(ctx.versionSelection),
      };
    },
    [activeWorkspaceId, interactionMode, isCompletionTemplate, numericPromptId, remixEnabled],
  );

  const getDeleteVariables = useCallback(
    (sessionId: string) => {
      if (!activeWorkspaceId || !numericPromptId) {
        return null;
      }
      return {
        workspace_id: activeWorkspaceId,
        shared_session_id: sessionId,
        prompt_id: numericPromptId,
      };
    },
    [activeWorkspaceId, numericPromptId],
  );

  const getUpdateVariables = useCallback(
    (
      sessionId: string,
      fields: Partial<
        Pick<
          SharedSession,
          | "name"
          | "is_public"
          | "batch_enabled"
          | "chat_enabled"
          | "remix_enabled"
          | "label_name"
          | "version_number"
        >
      >,
    ): UpdateSharedSessionRequest | null => {
      if (!activeWorkspaceId || !numericPromptId) {
        return null;
      }
      return {
        workspace_id: activeWorkspaceId,
        prompt_id: numericPromptId,
        shared_session_id: sessionId,
        ...fields,
      };
    },
    [activeWorkspaceId, numericPromptId],
  );

  const {
    open,
    mode,
    setMode,
    userChoseCreate,
    name,
    setName,
    isPublic,
    setIsPublic,
    versionSelection,
    setVersionSelection,
    newShareUrl,
    hasExistingSessions,
    isCheckingExistence,
    sessions,
    isLoadingSessions,
    resetForm,
    handleOpenChange,
    handleCreate,
    handleDeleteSession,
    handleUpdateSession,
    handleNavigateToManage,
    isCreateDisabled,
    hasSessionsToManage,
  } = useShareSessionDialog({
    queryTarget: {
      workspaceId: activeWorkspaceId ?? undefined,
      promptId: numericPromptId,
    },
    existsQueryEnabled: !!activeWorkspaceId && !!numericPromptId,
    resourceName: promptName ?? "",
    defaultVersionSelection,
    createMutation: sharePromptMutation,
    resetCreateMutation: () => sharePromptMutation.reset(),
    buildCreateVariables,
    getDeleteVariables,
    getUpdateVariables,
    resetExtraFormState,
    open: openProp,
    onOpenChange,
  });

  const handleNewShareLink = useCallback(() => {
    userChoseCreate.current = true;
    resetForm();
    setMode(ShareDialogMode.CREATE);
  }, [resetForm, setMode, userChoseCreate]);

  return (
    <ShareSessionDialogShell
      open={open}
      onOpenChange={handleOpenChange}
      hasExistingSessions={hasExistingSessions}
      isCheckingExistence={isCheckingExistence}
      sessions={sessions}
      triggerSize="sm"
      triggerAriaLabel="Share"
      hideTrigger={hideTrigger}
    >
      {mode === ShareDialogMode.CREATED && newShareUrl && (
        <CreatedView
          key="created"
          shareUrl={newShareUrl}
          isPublic={isPublic}
          hasExistingSessions={hasSessionsToManage}
          onManage={handleNavigateToManage}
        />
      )}

      {mode === ShareDialogMode.MANAGE && (
        <ManageView
          key="manage"
          sessions={sessions}
          isLoadingSessions={isLoadingSessions}
          onUpdate={handleUpdateSession}
          onDelete={handleDeleteSession}
          onNewLink={handleNewShareLink}
          hideModeSelector={isCompletionTemplate}
        />
      )}

      {mode === ShareDialogMode.CREATE && (
        <CreateView
          key="create"
          name={name}
          onNameChange={setName}
          isPublic={isPublic}
          onIsPublicChange={setIsPublic}
          mode={isCompletionTemplate ? "batch" : interactionMode}
          onModeChange={setInteractionMode}
          hideModeSelector={isCompletionTemplate}
          versionSelection={versionSelection}
          onVersionSelectionChange={setVersionSelection}
          remixEnabled={remixEnabled}
          onRemixEnabledChange={setRemixEnabled}
          hasExistingSessions={hasSessionsToManage}
          isCreateDisabled={isCreateDisabled}
          isPending={sharePromptMutation.isPending}
          onCreate={handleCreate}
          onBack={handleNavigateToManage}
        />
      )}
    </ShareSessionDialogShell>
  );
};
